import { useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/components/ui';
import { StockDetailModal } from './StockDetailModal';
import { searchStocks } from './stockApi';

/**
 * 종목 이름을 누르면 그 종목 팝업이 뜬다. (2026-09-19 사용자 요청, BACKLOG I-60)
 *
 * 사용자 - "화면별로 종목을 클릭하면 무조건 해당 종목에 대한 모달을 띄우도록.
 *   추천 프로필의 청산 완료 · 매매 이력에서 종목 열을 눌러도 반응이 없다."
 *   (회고 본문이나 설명 글 속 종목명은 제외 - 사용자 지정)
 *
 * 🔴 왜 화면마다 상태를 두지 않고 이 부품이 팝업을 들고 있나
 *   지금까지는 화면이 openStockId 를 들고 StockDetailModal 을 한 번 그렸다(BandPanel 등).
 *   표가 여러 하위 컴포넌트로 쪼개져 있으면 그 상태를 표마다 내려보내야 하고,
 *   새 표를 만들 때 한 줄을 빠뜨리면 "눌러도 반응 없음" 이 된다 - 이번 요청이 바로 그것이다.
 *   이름 칸에서 이 부품만 쓰면 팝업까지 따라온다.
 *
 * 🔴 팝업을 document.body 로 포털한다
 *   Modal 은 포털 없이 그 자리에 fixed 로 그려진다. 표 칸 안에 그리면
 *   ① 조상에 transform 이 있을 때 fixed 가 그 조상 기준이 되어 위치가 틀어지고
 *   ② React 이벤트는 포털이어도 컴포넌트 트리를 따라 올라가므로, 팝업 안 클릭이 행의
 *      onClick(펼치기 등)까지 번진다. 그래서 포털 바깥 한 겹에서 전파를 끊는다.
 *
 * 🔴 열렸을 때만 팝업을 마운트한다
 *   StockDetailModal 은 시세·차트·상세 훅을 여럿 부른다. 100행 표에서 행마다 미리 그려 두면
 *   닫혀 있어도 훅 100벌이 돈다.
 *
 * 종목 ID 가 없는 화면(내 계좌 보유 · 내 매매 기록 - 토스 원장이라 종목 코드만 있다)은
 * symbol 을 넘긴다. 누르는 순간 종목 검색으로 코드가 **정확히 같은** 종목을 찾아 연다.
 * 못 찾으면(상장폐지 등) 조용히 아무것도 하지 않는다 - 다른 종목을 여는 것보다 낫다.
 *
 * 둘 다 없으면 누를 수 없는 평범한 글자로 둔다.
 */
export function StockLink({
  stockId,
  symbol,
  country,
  children,
  className,
}: {
  stockId?: number | null;
  symbol?: string | null;
  country?: 'KR' | 'US';
  children: ReactNode;
  className?: string;
}) {
  const [openId, setOpenId] = useState<number | null>(null);

  const knownId = stockId ?? null;
  if (knownId === null && !symbol) {
    return <span className={className}>{children}</span>;
  }

  const open = async () => {
    if (knownId !== null) {
      setOpenId(knownId);
      return;
    }
    try {
      const code = symbol as string;
      const found = await searchStocks(code, country ?? countryOfSymbol(code));
      const exact = found.find((s) => s.symbol === code);
      if (exact) {
        setOpenId(exact.id);
      }
    } catch {
      // 검색 실패는 팝업을 안 여는 것으로 끝낸다. 화면의 다른 부분까지 막을 일이 아니다
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // 행 전체에 걸린 클릭(펼치기 등)이 함께 돌지 않게 한다
          e.stopPropagation();
          void open();
        }}
        className={cx(
          'cursor-pointer text-left underline-offset-2 hover:underline focus-visible:underline',
          className,
        )}
      >
        {children}
      </button>
      {openId !== null &&
        createPortal(
          // 포털이어도 React 이벤트는 이 컴포넌트의 조상(표의 행)으로 올라간다 - 여기서 끊는다
          <div onClick={(e) => e.stopPropagation()}>
            <StockDetailModal stockId={openId} onClose={() => setOpenId(null)} />
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * 종목 코드만 보고 시장을 추정한다. 주문 내역처럼 시장 칸이 없는 곳을 위해서다.
 *
 * 국내 코드는 숫자로 시작한다 - 005930(보통주), 0008Z0(ETF·신규 코드 체계).
 * 미국 티커는 영문이다 - AAPL, BRK.B.
 */
export function countryOfSymbol(symbol: string): 'KR' | 'US' {
  return /^\d/.test(symbol) ? 'KR' : 'US';
}
